import { Box } from "@mui/material";
import { DotLegalButton, DotLegalLink, DotLegalPaper } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import FilterBar from "@common/components/filterBar/FilterBar";
import { FilterOption } from "@common/components/filterPopover/FilterPopover.types";
import RiskGovernanceModeSwitcher, { RiskGovernanceMode } from "../riskArea/RiskGovernanceModeSwitcher";
import RiskGovernanceViewBanner from "./RiskGovernanceViewBanner";

interface RiskGovernanceFilterBarProps {
    isLoading: boolean;
    filterOptions: Array<FilterOption>;
    onFilterChange: (options: Array<FilterOption>) => void;
    pinnedFilterIds: Array<string>;
    onPinnedFilterIdsChange: (ids: Array<string> | undefined) => void;
    searchText: string;
    onSearchTextChange: (value: string) => void;
    hasActiveFilters: boolean;
    onClearFilter: () => void;
    mode: RiskGovernanceMode;
    onModeChange: (mode: RiskGovernanceMode) => void;
    onOpenHistory: () => void;
    isAsOf: boolean;
    asOf: string | null;
    historyLink?: string;
    onReturnToToday: () => void;
}

// The white bar below the stat cards (work item #6698). Hosts the operational-level filters and the governance
// mode selector, plus an info banner at the top when the overview is showing a non-current view (a point-in-time
// snapshot or the after-mitigation projection).
function RiskGovernanceFilterBar(props: RiskGovernanceFilterBarProps) {
    const { translateString } = useTranslation();

    return (
        <DotLegalPaper>
            <RiskGovernanceViewBanner
                isAsOf={props.isAsOf}
                asOf={props.asOf}
                afterMitigation={props.mode === RiskGovernanceMode.AfterMitigation}
                action={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {props.historyLink && (
                            <DotLegalLink to={props.historyLink} linkColor="primary">
                                {translateString("riskGovernanceViewInHistory")}
                            </DotLegalLink>
                        )}
                        <DotLegalButton buttonType="secondary" size="small" onClick={props.onReturnToToday}>
                            {translateString("riskGovernanceReturnToToday")}
                        </DotLegalButton>
                    </Box>
                }
            />
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
                <FilterBar
                    isLoading={props.isLoading}
                    fetchFilterData
                    filterData={props.filterOptions}
                    onMenuClose={props.onFilterChange}
                    onRemove={props.onFilterChange}
                    pinnedFilterIds={props.pinnedFilterIds}
                    onPinnedFilterIdsChange={props.onPinnedFilterIdsChange}
                    onClearFilter={props.hasActiveFilters ? props.onClearFilter : undefined}
                    clearFilterText={props.hasActiveFilters ? "defaultView" : undefined}
                    searchValue={props.searchText}
                    onSearchChange={props.onSearchTextChange}
                    searchLabel={translateString("search")}
                />
                <RiskGovernanceModeSwitcher
                    selected={props.mode}
                    onSelect={props.onModeChange}
                    onOpenSnapshot={props.onOpenHistory}
                    currentLabel={translateString("riskGovernanceModeCurrent")}
                    afterMitigationLabel={translateString("riskGovernanceModeAfterMitigation")}
                    snapshotLabel={translateString("riskGovernanceModeSnapshot")}
                    snapshotActive={props.isAsOf}
                    afterMitigationDisabled={props.isAsOf}
                    afterMitigationDisabledTooltip={translateString("riskGovernanceNotAvailableForPastDates")}
                />
            </Box>
        </DotLegalPaper>
    );
}

export default RiskGovernanceFilterBar;
